import React from 'react';
import { Link } from 'react-router-dom';
import { UtensilsCrossed, Coffee, Store, Cookie, ArrowUpRight } from 'lucide-react';
import { PublicSection, Reveal } from '@/components/public';

const CATEGORIES = [
  {
    icon: <UtensilsCrossed className="w-6 h-6" />,
    title: 'Restaurants',
    href: '/for-restaurants',
    description:
      'Full-service, quick-service, and family restaurants growing local delivery without the big-app squeeze.',
  },
  {
    icon: <Coffee className="w-6 h-6" />,
    title: 'Cafés & Bakeries',
    href: '/for-cafes-bakeries',
    description:
      'Coffee shops, bakeries, and dessert spots—with menus and pacing that actually fit your kitchen.',
  },
  {
    icon: <Store className="w-6 h-6" />,
    title: 'Grocery & Specialty',
    href: '/for-grocery-specialty',
    description:
      'Local grocers, butchers, liquor stores, and specialty retailers who want a cleaner digital storefront.',
  },
  {
    icon: <Cookie className="w-6 h-6" />,
    title: 'Ghost Kitchens & Pop-Ups',
    href: '/for-ghost-kitchens-popups',
    description:
      'Delivery-first and seasonal operators—simple to onboard, simple to pause.',
  },
];

/**
 * "Who it's for" — section 5. Editorial divided list: large typographic rows
 * that shift and turn red on hover. Deliberately different rhythm from the
 * bento (section 3) and support grid (section 6).
 */
export default function BuiltForSection() {
  return (
    <PublicSection
      id="built-for"
      testId="join-built-for"
      padBottom="pb-10 md:pb-14"
      eyebrow="Who it's for"
      title="Built for businesses like yours."
      description="Tell us what you do and we'll tailor onboarding to your setup. A short sample of the independent operators Hungry Canadian is built to support."
    >
      <div className="border-t border-border" data-testid="built-for-list">
        {CATEGORIES.map((c, i) => {
          const slug = c.title
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');
          return (
            <Reveal key={c.title} delay={i * 0.05}>
              <Link
                to={c.href}
                data-testid={`category-${slug}`}
                className="group grid md:grid-cols-[4rem_1fr_auto] items-center gap-4 md:gap-8 border-b border-border py-8 md:py-10 transition-colors hover:bg-card focus-visible:outline-none focus-visible:bg-card"
              >
                <div className="flex items-center gap-4 md:block">
                  <span className="text-sm font-heading font-black text-primary">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                </div>
                <div className="flex items-start gap-5">
                  <span className="hidden sm:inline-flex items-center justify-center w-12 h-12 bg-primary/10 text-primary shrink-0 transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                    {c.icon}
                  </span>
                  <div>
                    <h3 className="text-2xl md:text-4xl font-heading font-black tracking-tight text-foreground transition-colors group-hover:text-primary">
                      {c.title}
                    </h3>
                    <p className="mt-2 text-sm md:text-base text-muted-foreground leading-relaxed max-w-2xl">
                      {c.description}
                    </p>
                  </div>
                </div>
                <ArrowUpRight
                  aria-hidden
                  className="hidden md:block w-7 h-7 text-muted-foreground transition-[color,transform] duration-300 group-hover:text-primary group-hover:translate-x-1 group-hover:-translate-y-1"
                />
              </Link>
            </Reveal>
          );
        })}
      </div>
    </PublicSection>
  );
}
